import React, { useState } from "react";
import { Button } from "../components/Button.jsx";
import { useGame } from "@empirica/core/player/classic/react";
import { getTaskConfig } from "../utils/taskConfig.js";

export function AIDiscussInstructions({ next }) {
  const game = useGame();
  const taskConfig = getTaskConfig(game?.get("treatment") || {});
  const [answers, setAnswers] = useState({ q1: "", q2: "", q3: "", q4: "" });
  const [showError, setShowError] = useState(false);

  const correctAnswers = {
    q1: "detailed",
    q2: "group",
    q3: "summary",
    q4: "ai"
  };

  const handleAnswerChange = (question, value) => {
    setAnswers({ ...answers, [question]: value });
    setShowError(false);
  };

  const handleNext = () => {
    const allCorrect = Object.keys(correctAnswers).every(
      key => answers[key] === correctAnswers[key]
    );
    
    if (allCorrect) {
      next();
    } else {
      setShowError(true);
    }
  };

  return (
    <div className="mt-3 sm:mt-5 p-20">
      <h3 className="text-lg leading-6 font-medium text-gray-900">
        Instructions
      </h3>
      <div className="mt-3 mb-6 space-y-6 text-gray-800">
        {/* 1. Objective of the game */}
        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-3 text-lg">1. Objective of the Game</h4>
          <p>
            You will be playing this game with other participants. The objective is to make an accurate prediction, which will be determined by taking the <strong>majority vote</strong> of your group.
          </p>
          {taskConfig.introPrompt && (
            <p className="mt-2">{taskConfig.introPrompt}</p>
          )}
        </div>

        {/* 2. Process of the game */}
        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-3 text-lg">2. Process of the Game</h4>
          <div className="space-y-2">
            {taskConfig.quizEnabled && (
              <p>
                <strong>Step 0 (Quiz):</strong> You will first complete a <strong>10-question quiz</strong> based on loan cases. This quiz is used to evaluate baseline knowledge in the domain. You will not see the correct answers during the quiz.
              </p>
            )}
            <p>
              <strong>Step 1:</strong> You will make your <strong>own individual prediction</strong> and then <strong>converse with an AI assistant</strong> about your prediction and explain your rationale. Please provide <strong>a detailed explanation</strong> of your reasoning as it will be used to assess the quality of your participation.
            </p>
            <p>
              <strong>Step 2:</strong> After the conversation with the AI assistant, your AI assistant will discuss with other participants' AI assistants about every player's prediction and rationale, and you will see a <strong>summary of the discussion</strong>.
            </p>
            <p>
              <strong>Step 3:</strong> After viewing the summary, you will submit your <strong>final individual prediction</strong>.
            </p>
          </div>
        </div>

        {/* 3. Reward setup */}
        <div className="bg-blue-50 rounded-md shadow-sm border border-blue-200 p-4">
          <h4 className="font-semibold text-blue-900 mb-3 text-lg">3. Reward Setup</h4>
          <div className="space-y-2 text-blue-800">
            <p>
              <strong>Base Payment:</strong> You will receive $3 (roughly $9/hour).
            </p>
            {taskConfig.quizEnabled && (
              <p>
                <strong>Quiz Bonus:</strong> Quiz correctness is linked to bonus. Each correct quiz question is worth <strong>$0.3</strong>.
              </p>
            )}
            <p>
              <strong>Accuracy Bonus:</strong> In addition to the base payment, you may receive <strong>$3.5</strong> if the <strong>majority vote of your group</strong> (not your individual final prediction) is accurate.
            </p>
          </div>
        </div>

        {/* Comprehension Questions */}
        <div className="bg-yellow-50 rounded-md shadow-sm border border-yellow-200 p-4">
          <h4 className="font-semibold text-yellow-900 mb-3 text-lg">Please answer the following questions to proceed:</h4>
          
          <div className="space-y-4">
            {/* Question 1 */}
            <div>
              <p className="font-medium text-gray-900 mb-2">
                1. What should you do when you converse with the AI assistant about your prediction?
              </p>
              <div className="space-y-1 ml-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q1"
                    value="brief"
                    checked={answers.q1 === "brief"}
                    onChange={(e) => handleAnswerChange("q1", e.target.value)}
                    className="mr-2"
                  />
                  <span>Provide a brief one-sentence explanation</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q1"
                    value="detailed"
                    checked={answers.q1 === "detailed"}
                    onChange={(e) => handleAnswerChange("q1", e.target.value)}
                    className="mr-2"
                  />
                  <span>Explain your rationale and provide a detailed explanation of your reasoning</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q1"
                    value="skip"
                    checked={answers.q1 === "skip"}
                    onChange={(e) => handleAnswerChange("q1", e.target.value)}
                    className="mr-2"
                  />
                  <span>Skip the conversation and go directly to the next step</span>
                </label>
              </div>
            </div>

            {/* Question 2 */}
            <div>
              <p className="font-medium text-gray-900 mb-2">
                2. The $3.5 accuracy bonus is based on which outcome?
              </p>
              <div className="space-y-1 ml-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q2"
                    value="individual"
                    checked={answers.q2 === "individual"}
                    onChange={(e) => handleAnswerChange("q2", e.target.value)}
                    className="mr-2"
                  />
                  <span>Your individual final prediction</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q2"
                    value="group"
                    checked={answers.q2 === "group"}
                    onChange={(e) => handleAnswerChange("q2", e.target.value)}
                    className="mr-2"
                  />
                  <span>The majority vote of your group</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q2"
                    value="initial"
                    checked={answers.q2 === "initial"}
                    onChange={(e) => handleAnswerChange("q2", e.target.value)}
                    className="mr-2"
                  />
                  <span>Your initial prediction</span>
                </label>
              </div>
            </div>

            {/* Question 3 */}
            <div>
              <p className="font-medium text-gray-900 mb-2">
                3. What happens after your AI assistant discusses with other participants' AI assistants?
              </p>
              <div className="space-y-1 ml-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q3"
                    value="discussion"
                    checked={answers.q3 === "discussion"}
                    onChange={(e) => handleAnswerChange("q3", e.target.value)}
                    className="mr-2"
                  />
                  <span>You will enter a group discussion</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q3"
                    value="summary"
                    checked={answers.q3 === "summary"}
                    onChange={(e) => handleAnswerChange("q3", e.target.value)}
                    className="mr-2"
                  />
                  <span>You will see a summary of the discussion</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q3"
                    value="skip"
                    checked={answers.q3 === "skip"}
                    onChange={(e) => handleAnswerChange("q3", e.target.value)}
                    className="mr-2"
                  />
                  <span>You will skip to the final prediction</span>
                </label>
              </div>
            </div>

            {/* Question 4 */}
            <div>
              <p className="font-medium text-gray-900 mb-2">
                4. What happens after you converse with the AI assistant?
              </p>
              <div className="space-y-1 ml-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q4"
                    value="ai"
                    checked={answers.q4 === "ai"}
                    onChange={(e) => handleAnswerChange("q4", e.target.value)}
                    className="mr-2"
                  />
                  <span>Your AI assistant will discuss with other participants' AI assistants and you will see a summary</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q4"
                    value="direct"
                    checked={answers.q4 === "direct"}
                    onChange={(e) => handleAnswerChange("q4", e.target.value)}
                    className="mr-2"
                  />
                  <span>You will directly submit your final prediction</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q4"
                    value="always"
                    checked={answers.q4 === "always"}
                    onChange={(e) => handleAnswerChange("q4", e.target.value)}
                    className="mr-2"
                  />
                  <span>You will always enter a group discussion</span>
                </label>
              </div>
            </div>
          </div>

          {showError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 text-sm">
                Please answer all questions correctly before proceeding.
              </p>
            </div>
          )}
        </div>
      </div>
      <Button handleClick={handleNext} autoFocus>
        <p>Next</p>
      </Button>
    </div>
  );
}
